// Tarefas que o resumo da reunião encontrou: lista com caixas de seleção e
// "Criar no quadro". Com "Criar sempre automaticamente", as próximas reuniões
// já criam as tarefas no quadro escolhido assim que o resumo fica pronto.

import * as store from "./store.js";
import * as router from "./router.js";
import { api } from "./api.js";
import { el, toast, titleOf, safeGet, safeSet } from "./dom.js";
import { iconText } from "./icons.js";

const DB_KEY = "ordo.meetingTasksDb";
const AUTO_KEY = "ordo.meetingTasksAuto";
const running = new Set();

const norm = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

// Quadro preferido: o último usado para tarefas de reunião (ou do "Transformar em tarefas").
function boards() {
  return store.databases().filter((d) => store.canEdit(d) && !d.archived_at);
}
function preferredBoard() {
  const list = boards();
  let last = "";
  try { last = localStorage.getItem("ordo.tasksDb") || ""; } catch { /* sem armazenamento */ }
  const want = safeGet(DB_KEY, "") || last;
  return list.find((d) => d.id === want) || null;
}

// Responsável citado na reunião -> pessoa do Ordo (nome completo ou primeiro nome).
function personFor(name) {
  const n = norm(name);
  if (!n) return null;
  const users = store.state.users || [];
  return users.find((u) => norm(u.name) === n)
    || users.find((u) => norm(u.name).split(/\s+/)[0] === n.split(/\s+/)[0])
    || null;
}

function pick(props, type, prefer) {
  const list = props.filter((p) => p.type === type);
  return list.find((p) => prefer.test(norm(p.name))) || list[0] || null;
}

// Cria as tarefas marcadas como linhas do quadro e avisa o servidor.
export async function createTasks(meeting, items, dbId) {
  if (running.has(meeting.id)) return 0;
  running.add(meeting.id);
  try {
    const db = await store.load(dbId);
    const props = db.page.schema?.properties || [];
    const person = pick(props, "person", /respons|dono|pessoa|assign|quem/);
    const date = pick(props, "date", /prazo|entrega|vencimento|due|data/);
    const origin = `Da reunião "${meeting.title || "Reunião"}" (${new Date(meeting.started_at).toLocaleDateString("pt-BR")}).`;
    for (const t of items) {
      const p = {};
      const who = t.owner && personFor(t.owner);
      if (person && who) p[person.id] = [who.id];
      if (date && t.due) p[date.id] = t.due;
      const paras = [t.description, t.owner && !who ? `Responsável citado: ${t.owner}.` : null, origin].filter(Boolean);
      store.create({ parent_id: dbId, title: t.title, props: p,
        content: { type: "doc", content: paras.map((text) => ({ type: "paragraph", content: [{ type: "text", text }] })) } });
    }
    await api("PATCH", `/meetings/${meeting.id}`, { tasks_created: { db_id: dbId, count: items.length } });
    meeting.tasks = { ...(meeting.tasks || {}), created: { db_id: dbId, count: items.length, at: Date.now() } };
    safeSet(DB_KEY, dbId);
    return items.length;
  } finally {
    running.delete(meeting.id);
  }
}

// Resumo pronto com "Criar sempre automaticamente" ligado: cria sem perguntar.
export async function autoCreate(meeting) {
  const items = meeting.tasks?.items || [];
  if (!items.length || meeting.tasks.created || !safeGet(AUTO_KEY, false)) return false;
  if (meeting.user_id !== store.state.me?.id) return false;
  const db = preferredBoard();
  if (!db) return false;
  const n = await createTasks(meeting, items, db.id).catch(() => 0);
  if (n) toast(`${n} tarefa(s) da reunião criada(s) em ${titleOf(db)}.`);
  return n > 0;
}

// Painel abaixo do resumo. onChange() redesenha o bloco depois de criar.
export function tasksPanel(meeting, { editable, onChange }) {
  const items = meeting.tasks?.items || [];
  if (!items.length) return null;
  const created = meeting.tasks.created;
  if (created) {
    const db = store.node(created.db_id);
    return el("div", { class: "mb-tasks done" },
      el("span", {}, `✓ ${created.count} tarefa(s) criada(s) no quadro `),
      db ? el("button", { class: "link", onclick: () => router.openPage(db.id) }, `${iconText(db.icon, "▦")} ${titleOf(db)}`) : el("span", {}, "(quadro apagado)"));
  }
  const rows = items.map((t) => ({ t, on: true }));
  const list = el("div", { class: "task-preview" }, rows.map((r) => el("label", { class: "task-row" },
    el("input", { type: "checkbox", checked: true, disabled: !editable, onchange: (e) => { r.on = e.target.checked; sync(); } }),
    el("div", {}, el("strong", {}, r.t.title),
      r.t.description ? el("div", { class: "muted small" }, r.t.description) : null,
      r.t.owner || r.t.due ? el("div", { class: "muted small" }, [r.t.owner, r.t.due && new Date(r.t.due + "T12:00").toLocaleDateString("pt-BR")].filter(Boolean).join(" · ")) : null))));
  const head = el("div", { class: "mb-tasks-head" }, el("strong", {}, `Tarefas pendentes encontradas (${items.length})`));
  if (!editable) return el("div", { class: "mb-tasks" }, head, list);

  const all = boards();
  if (!all.length) {
    return el("div", { class: "mb-tasks" }, head, list,
      el("p", { class: "muted small" }, "Crie um banco de dados (quadro) para receber as tarefas das reuniões."));
  }
  const pref = preferredBoard();
  const sel = el("select", {}, all.map((d) => el("option", { value: d.id, selected: d.id === pref?.id }, `${iconText(d.icon, "▦")} ${titleOf(d)}`)));
  const auto = el("input", { type: "checkbox", checked: !!safeGet(AUTO_KEY, false),
    onchange: (e) => safeSet(AUTO_KEY, e.target.checked) });
  const go = el("button", { class: "primary", onclick: async () => {
    const chosen = rows.filter((r) => r.on).map((r) => r.t);
    go.disabled = true;
    try {
      const n = await createTasks(meeting, chosen, sel.value);
      toast(`${n} tarefa(s) criada(s) em ${titleOf(store.node(sel.value))}.`);
      if (auto.checked) safeSet(DB_KEY, sel.value);
      onChange?.();
    } catch (err) {
      toast("Não foi possível criar as tarefas: " + err.message);
      go.disabled = false;
    }
  } }, "Criar no quadro");
  const sync = () => {
    const n = rows.filter((r) => r.on).length;
    go.disabled = !n;
    go.textContent = n === 1 ? "Criar 1 tarefa no quadro" : `Criar ${n} tarefas no quadro`;
  };
  sync();
  return el("div", { class: "mb-tasks" }, head, list,
    el("div", { class: "mb-tasks-actions" }, sel, go),
    el("label", { class: "check small" }, auto, " Criar sempre automaticamente neste quadro quando o resumo ficar pronto"));
}
